// v49 RRHH — /app/rrhh/expedientes
// Listado del expediente digital: buscador, filtro por estado, badge de
// expediente incompleto (D4, completitud calculada por el backend) y alta.
// El detalle vive en /app/rrhh/expedientes/:id.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config/api';
import { formatFecha } from '../utils/fecha';
import {
  cargarListas, ESTADOS_LABORALES, TIPOS_CONTRATO, CONTRATOS_CON_VENCIMIENTO,
  MODALIDADES_REMOTAS, ETIQUETA_CAMPO, msgError,
  inputStyle, btnPrimario, btnNavy, btnSecundario, tarjeta, thStyle
} from '../utils/rrhh';
import { Campo, CampoCheck, Seccion, Badge, Aviso, tonoEstadoLaboral } from '../components/RrhhCampos';

const EMPTY = {
  identificacion: '', nombre_completo: '', fecha_nacimiento: '', nacionalidad: '',
  estado_civil_id: null, direccion: '', telefono_principal: '', telefono_secundario: '',
  correo_personal: '', correo_empresarial: '',
  incorporado_colegio: false, colegio_profesional_id: null,
  tiene_licencia_conducir: false, licencia_vence: '',
  fecha_ingreso: '', puesto_id: null, departamento_id: null, jefatura_id: null,
  jornada_id: null, horario: '', modalidad_id: null, direccion_teletrabajo: '',
  estado_laboral: 'Preingreso',
  tipo_contrato: '', contrato_inicia: '', contrato_vence: '', periodo_prueba_hasta: '',
  salario: '', periodicidad_pago_id: null, moneda: 'Colones', banco_id: null, cuenta_iban: ''
};

export default function RrhhExpedientes() {
  const navigate = useNavigate();

  const [lista, setLista]       = useState([]);
  const [listas, setListas]     = useState({});
  const [alertas, setAlertas]   = useState(null);
  const [verAlertas, setVerAlertas] = useState(false);
  const [filtro, setFiltro]     = useState('');
  const [estado, setEstado]     = useState('Activo');
  const [cargando, setCargando] = useState(true);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [guardando, setGuardando] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { cargar(); }, [estado]);       // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargarAuxiliares(); }, []);

  const cargar = async () => {
    setCargando(true);
    try {
      const qs = estado ? `?estado=${encodeURIComponent(estado)}` : '';
      const r = await axios.get(`${API}/rrhh-colaboradores${qs}`);
      setLista(r.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  };

  const cargarAuxiliares = async () => {
    try { setListas(await cargarListas()); } catch (e) { /* el form lo muestra vacio */ }
    try {
      const r = await axios.get(`${API}/rrhh-colaboradores?action=alertas`);
      setAlertas(r.data);
    } catch (e) { /* las alertas no bloquean el listado */ }
  };

  const filtrados = lista.filter(c =>
    [c.codigo, c.nombre_completo, c.puesto, c.departamento]
      .some(v => (v || '').toLowerCase().includes(filtro.toLowerCase()))
  );

  const incompletos = lista.filter(c => !c.expediente_completo).length;
  const totalAlertas = alertas
    ? alertas.documentos.length + alertas.contratos.length + alertas.incompletos.length
      + (alertas.licencias?.length || 0)
    : 0;

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const abrirNuevo = () => { setForm(EMPTY); setError(''); setModal(true); };

  const guardar = async () => {
    if (!form.identificacion?.trim() || !form.nombre_completo?.trim()) {
      setError('La identificacion y el nombre completo son obligatorios.');
      return;
    }
    if (exigeVencimiento && !form.contrato_vence) {
      setError(`Un contrato ${form.tipo_contrato} requiere fecha de vencimiento.`);
      return;
    }
    if (esRemota && !form.direccion_teletrabajo?.trim()) {
      setError(`La modalidad ${modalidadSel.valor} requiere la direccion de teletrabajo.`);
      return;
    }
    setGuardando(true); setError('');
    // Los vacios van como null: el backend distingue null de '' para las FK.
    const body = {};
    Object.entries(form).forEach(([k, v]) => { body[k] = (v === '' ? null : v); });
    try {
      const r = await axios.post(`${API}/rrhh-colaboradores`, body);
      setModal(false);
      navigate(`/app/rrhh/expedientes/${r.data.id}`);
    } catch (e) { setError(msgError(e)); }
    finally { setGuardando(false); }
  };

  const opciones = (l) => listas[l] || [];

  // Campos condicionales del alta: la modalidad decide si aplica la direccion de
  // teletrabajo, y el tipo de contrato si aplica la fecha de vencimiento.
  const modalidadSel = (listas.rrhh_modalidad_trabajo || []).find(m => String(m.id) === String(form.modalidad_id));
  const esRemota = !!modalidadSel && MODALIDADES_REMOTAS.includes(modalidadSel.valor);
  const exigeVencimiento = CONTRATOS_CON_VENCIMIENTO.includes(form.tipo_contrato);

  return (
    <div style={{ padding: '24px', fontFamily: 'Segoe UI, sans-serif', maxWidth: '1200px' }}>

      {/* Header */}
      <div style={{ background: '#012E40', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ color: '#fff', fontSize: '17px', fontWeight: '700' }}>Expedientes</span>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2px' }}>
            {[['Activo', 'Activos'], ['Inactivo', 'Inactivos'], ['', 'Todos']].map(([val, txt]) => (
              <button key={txt} onClick={() => setEstado(val)}
                style={{
                  padding: '4px 16px', border: 'none', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                  background: estado === val ? '#fff' : 'transparent',
                  color: estado === val ? '#012E40' : 'rgba(255,255,255,0.6)'
                }}>
                {txt}
              </button>
            ))}
          </div>
        </div>
        <button onClick={abrirNuevo} style={btnPrimario}>+ Nuevo expediente</button>
      </div>

      {/* Alertas (Etapa 11) — todas calculadas al vuelo */}
      {alertas && (
        <div style={{ ...tarjeta, marginBottom: '16px', overflow: 'hidden' }}>
          <div onClick={() => setVerAlertas(v => !v)}
            style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: totalAlertas ? '#FDF4E0' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#012E40', fontWeight: '600' }}>
              Alertas
              {totalAlertas > 0
                ? <Badge texto={`${totalAlertas} pendiente${totalAlertas !== 1 ? 's' : ''}`} tono="ambar" />
                : <Badge texto="Sin alertas" tono="verde" />}
            </div>
            <span style={{ color: '#888', fontSize: '12px' }}>{verAlertas ? 'Ocultar' : 'Ver detalle'}</span>
          </div>

          {verAlertas && (
            <div style={{ padding: '4px 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <BloqueAlerta titulo="Documentos vencidos o por vencer" vacio="Ningun documento por vencer."
                filas={alertas.documentos.map(d => ({
                  id: `doc-${d.id}`, colaborador_id: d.colaborador_id,
                  texto: `${d.codigo} · ${d.nombre_completo} — ${d.tipo}`,
                  extra: formatFecha(d.fecha_vencimiento),
                  tono: d.estado === 'vencido' ? 'rojo' : 'ambar',
                  etiqueta: d.estado === 'vencido' ? 'Vencido' : 'Por vencer'
                }))} onIr={navigate} />

              <BloqueAlerta titulo="Contratos por vencer" vacio="Ningun contrato por vencer."
                filas={alertas.contratos.map(c => ({
                  id: `con-${c.id}`, colaborador_id: c.id,
                  texto: `${c.codigo} · ${c.nombre_completo}`,
                  extra: formatFecha(c.contrato_vence), tono: 'ambar', etiqueta: c.tipo_contrato || 'Vence'
                }))} onIr={navigate} />

              {/* Solo aparece quien tiene licencia B: es la que habilita
                  conducir en giras, el resto de tipos es informativo (A5). */}
              <BloqueAlerta titulo="Licencias de conducir por vencer" vacio="Ninguna licencia por vencer."
                filas={(alertas.licencias || []).map(l => ({
                  id: `lic-${l.id}`, colaborador_id: l.id,
                  texto: `${l.codigo} · ${l.nombre_completo}`,
                  extra: formatFecha(l.licencia_vence), tono: 'ambar', etiqueta: 'Licencia B'
                }))} onIr={navigate} />

              <BloqueAlerta titulo="Expedientes incompletos" vacio="Todos los expedientes activos estan completos."
                filas={alertas.incompletos.map(c => ({
                  id: `inc-${c.id}`, colaborador_id: c.id,
                  texto: `${c.codigo} · ${c.nombre_completo}`,
                  extra: '', tono: 'gris', etiqueta: 'Incompleto'
                }))} onIr={navigate} />

              <BloqueAlerta titulo="Cambios recientes (30 dias)" vacio="Sin acciones aplicadas en el ultimo mes."
                filas={alertas.cambios.map(a => ({
                  id: `cam-${a.id}`, colaborador_id: a.colaborador_id,
                  texto: `${a.codigo} · ${a.nombre_completo}`,
                  extra: formatFecha(a.fecha_aplicacion), tono: 'verde', etiqueta: a.tipo
                }))} onIr={navigate} />
            </div>
          )}
        </div>
      )}

      {/* Buscador */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
        <input value={filtro} onChange={e => setFiltro(e.target.value)}
          placeholder="Buscar por codigo, nombre, puesto o departamento..."
          style={{ ...inputStyle, borderRadius: '10px' }} />
        <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>
          {filtrados.length} de {lista.length}
          {incompletos > 0 && <span style={{ color: '#8A6100' }}> · {incompletos} incompleto{incompletos !== 1 ? 's' : ''}</span>}
        </span>
      </div>

      {/* Tabla */}
      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        {cargando ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Cargando...</div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#bbb' }}>No se encontraron expedientes.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Codigo', 'Nombre completo', 'Puesto', 'Departamento', 'Ingreso', 'Estado laboral', 'Expediente'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.map(c => (
                <tr key={c.id} onClick={() => navigate(`/app/rrhh/expedientes/${c.id}`)}
                  style={{ borderBottom: '1px solid #f3f3f3', cursor: 'pointer', opacity: c.estado_expediente === 'Inactivo' ? 0.6 : 1 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px 14px', fontWeight: '600', color: '#012E40' }}>{c.codigo}</td>
                  <td style={{ padding: '10px 14px', color: '#1a1a1a', fontWeight: '500' }}>
                    {c.nombre_completo}
                    {c.estado_expediente === 'Inactivo' && <span style={{ marginLeft: 8 }}><Badge texto="Inactivo" tono="gris" /></span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#555' }}>{c.puesto || '—'}</td>
                  <td style={{ padding: '10px 14px', color: '#555' }}>{c.departamento || '—'}</td>
                  <td style={{ padding: '10px 14px', color: '#555' }}>{formatFecha(c.fecha_ingreso)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <Badge texto={c.estado_laboral || '—'} tono={tonoEstadoLaboral(c.estado_laboral)} />
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {c.expediente_completo
                      ? <Badge texto="Completo" tono="verde" />
                      : <span title={`Falta: ${(c.campos_faltantes || []).map(x => ETIQUETA_CAMPO[x] || x).join(', ')}`}>
                          <Badge texto={`Incompleto (${(c.campos_faltantes || []).length})`} tono="ambar" />
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de alta */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '860px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ background: '#012E40', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: '15px', fontWeight: '700' }}>Nuevo expediente</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>&#10005;</button>
            </div>

            <div style={{ overflowY: 'auto', padding: '24px' }}>
              <Aviso texto={error} />
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
                El codigo RH-### lo asigna el sistema al guardar y no se puede editar despues (D1).
              </div>

              <Seccion titulo="Datos personales">
                <Campo label="Identificacion" requerido valor={form.identificacion} onChange={v => f('identificacion', v)} placeholder="Sin guiones" />
                <Campo label="Nombre completo" requerido ancho={2} valor={form.nombre_completo} onChange={v => f('nombre_completo', v)} />
                <Campo label="Fecha de nacimiento" tipo="date" valor={form.fecha_nacimiento} onChange={v => f('fecha_nacimiento', v)} />
                <Campo label="Nacionalidad" valor={form.nacionalidad} onChange={v => f('nacionalidad', v)} />
                <Campo label="Estado civil" opciones={opciones('rrhh_estado_civil')} valor={form.estado_civil_id} onChange={v => f('estado_civil_id', v)} />
                <Campo label="Telefono principal" valor={form.telefono_principal} onChange={v => f('telefono_principal', v)} />
                <Campo label="Telefono secundario" valor={form.telefono_secundario} onChange={v => f('telefono_secundario', v)} />
                <Campo label="Correo personal" tipo="email" valor={form.correo_personal} onChange={v => f('correo_personal', v)} />
                <Campo label="Correo empresarial" tipo="email" valor={form.correo_empresarial} onChange={v => f('correo_empresarial', v)} />
                <Campo label="Direccion" ancho={2} valor={form.direccion} onChange={v => f('direccion', v)} />
                <CampoCheck label="Incorporado a colegio" valor={form.incorporado_colegio} onChange={v => f('incorporado_colegio', v)} />
                {form.incorporado_colegio && (
                  <Campo label="Colegio profesional" ancho={2} opciones={opciones('rrhh_colegio_profesional')} valor={form.colegio_profesional_id} onChange={v => f('colegio_profesional_id', v)} />
                )}
                <CampoCheck label="Licencia de conducir" valor={form.tiene_licencia_conducir} onChange={v => f('tiene_licencia_conducir', v)} />
                {form.tiene_licencia_conducir && (
                  <Campo label="Vence (licencia B)" tipo="date" valor={form.licencia_vence} onChange={v => f('licencia_vence', v)} />
                )}
              </Seccion>

              <Seccion titulo="Datos laborales">
                <Campo label="Fecha de ingreso" tipo="date" valor={form.fecha_ingreso} onChange={v => f('fecha_ingreso', v)} />
                <Campo label="Puesto" opciones={opciones('rrhh_puesto')} valor={form.puesto_id} onChange={v => f('puesto_id', v)} />
                <Campo label="Departamento" opciones={opciones('rrhh_departamento')} valor={form.departamento_id} onChange={v => f('departamento_id', v)} />
                <Campo label="Jefatura" opciones={lista.map(c => ({ id: c.id, valor: c.nombre_completo }))} valor={form.jefatura_id} onChange={v => f('jefatura_id', v)} />
                <Campo label="Jornada laboral" opciones={opciones('rrhh_jornada')} valor={form.jornada_id} onChange={v => f('jornada_id', v)} />
                <Campo label="Modalidad" opciones={opciones('rrhh_modalidad_trabajo')} valor={form.modalidad_id} onChange={v => f('modalidad_id', v)} />
                <Campo label="Estado laboral" opciones={ESTADOS_LABORALES.map(e => ({ id: e, valor: e }))} valor={form.estado_laboral} onChange={v => f('estado_laboral', v)} />
                <Campo label="Horario" ancho={esRemota ? 1 : 3} valor={form.horario} onChange={v => f('horario', v)} />
                {/* Solo con Teletrabajo o Hibrida (§5.1) */}
                {esRemota && (
                  <Campo label="Direccion de teletrabajo" requerido ancho={2} valor={form.direccion_teletrabajo} onChange={v => f('direccion_teletrabajo', v)} />
                )}
              </Seccion>

              <Seccion titulo="Contrato">
                <Campo label="Tipo de contrato" opciones={TIPOS_CONTRATO.map(t => ({ id: t, valor: t }))} valor={form.tipo_contrato} onChange={v => f('tipo_contrato', v)} />
                <Campo label="Inicia" tipo="date" valor={form.contrato_inicia} onChange={v => f('contrato_inicia', v)} />
                {/* Permanente no lleva vencimiento (§5.2) */}
                {exigeVencimiento && (
                  <Campo label="Vence" requerido tipo="date" valor={form.contrato_vence} onChange={v => f('contrato_vence', v)} />
                )}
                <Campo label="Periodo de prueba hasta" tipo="date" valor={form.periodo_prueba_hasta} onChange={v => f('periodo_prueba_hasta', v)} />
              </Seccion>

              <Seccion titulo="Salario y cuenta">
                <Campo label="Salario" tipo="number" valor={form.salario} onChange={v => f('salario', v)} />
                <Campo label="Moneda" opciones={[{ id: 'Colones', valor: 'Colones' }, { id: 'Dólares', valor: 'Dolares' }]} valor={form.moneda} onChange={v => f('moneda', v)} />
                <Campo label="Periodicidad de pago" opciones={opciones('rrhh_periodicidad_pago')} valor={form.periodicidad_pago_id} onChange={v => f('periodicidad_pago_id', v)} />
                <Campo label="Banco" opciones={opciones('rrhh_banco')} valor={form.banco_id} onChange={v => f('banco_id', v)} />
                <Campo label="Cuenta IBAN" ancho={2} valor={form.cuenta_iban} onChange={v => f('cuenta_iban', v)} placeholder="CR05015202001026284066" />
              </Seccion>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
              <button onClick={() => setModal(false)} style={btnSecundario}>Cancelar</button>
              <button onClick={guardar} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
                {guardando ? 'Guardando...' : 'Crear expediente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bloque de una categoria de alertas dentro del panel.
function BloqueAlerta({ titulo, filas, vacio, onIr }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
        {titulo}
      </div>
      {filas.length === 0
        ? <div style={{ fontSize: '12px', color: '#bbb' }}>{vacio}</div>
        : filas.map(fl => (
          <div key={fl.id} onClick={() => onIr(`/app/rrhh/expedientes/${fl.colaborador_id}`)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', fontSize: '12px', color: '#555', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}>
            <Badge texto={fl.etiqueta} tono={fl.tono} />
            <span style={{ flex: 1 }}>{fl.texto}</span>
            <span style={{ color: '#999' }}>{fl.extra}</span>
          </div>
        ))}
    </div>
  );
}
